import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const SettingsPage = () => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { changePassword, user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const forceChange = searchParams.get('force_change') === 'true';

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast.error('Please login to access settings');
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  if (authLoading) {
    return <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
      <div className="text-slate-900">Loading...</div>
    </div>;
  }

  if (!isAuthenticated) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setLoading(true);

    const result = await changePassword(oldPassword, newPassword);

    if (result.success) {
      setSuccess('Password changed successfully!');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Redirect to home after successful password change
      if (forceChange) {
        toast.success('Password changed successfully! You can now access the application.');
        setTimeout(() => {
          navigate('/');
        }, 1500);
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
      {/* Mandatory Password Change Modal Overlay */}
      {forceChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" />
      )}
      
      <div className={`max-w-2xl mx-auto ${forceChange ? 'relative z-50' : ''}`}>
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {forceChange && (
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-amber-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-amber-800">Password Change Required</h3>
                  <p className="mt-1 text-sm text-amber-700">
                    For security reasons, you must change your password before continuing.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            {forceChange ? 'Change Your Password' : 'Account Settings'}
          </h1>
          <p className="text-slate-600 mb-8">
            {forceChange 
              ? 'Please set a new secure password to continue' 
              : 'Manage your account preferences'
            }
          </p>

          {/* User Info - Only show if not force change */}
          {!forceChange && (
            <div className="bg-slate-50 rounded-lg p-6 mb-8">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Account Information</h2>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-600">Username:</span>
                  <span className="font-semibold text-slate-900">{user?.username}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Role:</span>
                  <span className="font-semibold text-blue-600">
                    {user?.is_admin ? 'Administrator' : 'User'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Change Password Form */}
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              {forceChange ? 'Set New Password' : 'Change Password'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                  {success}
                </div>
              )}

              <div>
                <label htmlFor="oldPassword" className="block text-sm font-medium text-slate-700 mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  id="oldPassword"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="Enter current password"
                  required
                />
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-slate-700 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="Enter new password (min 6 characters)"
                  required
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="Confirm new password"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Changing Password...' : 'Change Password'}
              </button>
              
              {!forceChange && (
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="w-full bg-slate-200 text-slate-700 py-3 rounded-lg font-semibold hover:bg-slate-300 transition"
                >
                  Cancel
                </button>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
